import { Component, OnDestroy, OnInit } from '@angular/core';
import { ControlsocialService } from '../../services/controlsocial.service';
import { UserSessionService } from '../../services/user-session.service';

type FeedbackType = 'success' | 'error' | '';
type ControlsocialTab = 'channel' | 'inbox';

@Component({
  selector: 'app-controlsocial',
  templateUrl: './controlsocial.component.html',
  styleUrls: ['./controlsocial.component.scss']
})
export class ControlsocialComponent implements OnInit, OnDestroy {
  tenantId: number | null = null;
  userId: number | null = null;

  activeTab: ControlsocialTab = 'channel';
  feedback = '';
  feedbackType: FeedbackType = '';

  loadingChannel = false;
  savingChannel = false;
  loadingConversations = false;
  loadingMessages = false;
  sendingMessage = false;
  showChannelForm = true;

  channel: any | null = null;
  channels: any[] = [];
  editingChannelId: number | null = null;
  channelForm = this.buildDefaultChannelForm();

  search = '';
  statusFilter = '';
  conversations: any[] = [];
  selectedConversation: any | null = null;
  messages: any[] = [];

  outboundTo = '';
  outboundText = '';
  private refreshIntervalId: number | null = null;

  constructor(
    private controlsocialService: ControlsocialService,
    private userSession: UserSessionService
  ) {}

  ngOnInit(): void {
    const user = this.userSession.getUser();
    this.tenantId = Number(user?.tenant_id || user?.tenant?.id || 0) || null;
    this.userId = Number(user?.id || user?.['user_id'] || 0) || null;

    if (!this.tenantId || !this.userId) {
      this.showFeedback('Sessão inválida para carregar o ControlSocial.', 'error');
      return;
    }

    this.channelForm.webhook_url = `${window.location.origin}/controlsocial/webhook`;
    this.loadChannel();
    this.loadConversations();
    this.refreshIntervalId = window.setInterval(() => this.refreshInboxSilently(), 12000);
  }

  ngOnDestroy(): void {
    if (this.refreshIntervalId !== null) {
      window.clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }

  get totalConversations(): number {
    return this.conversations.length;
  }

  get openConversations(): number {
    return this.conversations.filter((item) => String(item?.status || '').toUpperCase() === 'OPEN').length;
  }

  get unreadConversations(): number {
    return this.conversations.filter((item) => Number(item?.unread_count || 0) > 0).length;
  }

  get activeChannelLabel(): string {
    if (!this.channel) return 'Canal não configurado';
    const base = this.channel.channel_name || 'WhatsApp';
    const number = this.channel.business_phone_number || 'sem número';
    return `${base} · ${number}`;
  }

  get hasChannels(): boolean {
    return this.channels.length > 0;
  }

  private buildDefaultChannelForm() {
    return {
      channel_name: 'WhatsApp',
      phone_number_id: '',
      waba_id: '',
      business_phone_number: '',
      access_token: '',
      webhook_verify_token: '',
      app_secret: '',
      webhook_url: '',
      status: 'ACTIVE'
    };
  }

  setActiveTab(tab: ControlsocialTab) {
    this.activeTab = tab;
    if (tab === 'inbox') {
      this.loadConversations();
      if (this.selectedConversation?.id) {
        this.loadMessages(this.selectedConversation.id);
      }
    }
  }

  showFeedback(message: string, type: FeedbackType) {
    this.feedback = message;
    this.feedbackType = type;
  }

  clearFeedback() {
    this.feedback = '';
    this.feedbackType = '';
  }

  private normalizePhone(value: string): string {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const hasPlus = raw.startsWith('+');
    const digits = raw.replace(/\D/g, '');
    return hasPlus ? `+${digits}` : digits;
  }

  private trim(value: any): string {
    return String(value ?? '').trim();
  }

  loadChannel() {
    if (!this.tenantId) return;
    this.loadingChannel = true;
    this.controlsocialService.getChannel(this.tenantId).subscribe({
      next: (response) => {
        this.channel = response?.channel || null;
        this.channels = this.channel ? [this.channel] : [];
        this.editingChannelId = this.channel ? Number(this.channel.id) : null;
        if (this.channel) {
          this.channelForm = {
            channel_name: this.channel.channel_name || 'WhatsApp',
            phone_number_id: this.channel.phone_number_id || '',
            waba_id: this.channel.waba_id || '',
            business_phone_number: this.channel.business_phone_number || '',
            access_token: '',
            webhook_verify_token: '',
            app_secret: '',
            webhook_url: this.channel.webhook_url || `${window.location.origin}/controlsocial/webhook`,
            status: this.channel.status || 'ACTIVE'
          };
          this.showChannelForm = false;
        } else {
          this.channelForm = this.buildDefaultChannelForm();
          this.channelForm.webhook_url = `${window.location.origin}/controlsocial/webhook`;
          this.showChannelForm = true;
          this.editingChannelId = null;
        }
        this.loadingChannel = false;
      },
      error: (error) => {
        this.loadingChannel = false;
        this.showFeedback(error?.error?.error || 'Não foi possível carregar o canal.', 'error');
      }
    });
  }

  saveChannel() {
    if (!this.tenantId || !this.userId) return;
    this.clearFeedback();
    this.savingChannel = true;

    const payload = {
      tenant_id: this.tenantId,
      user_id: this.userId,
      channel_name: this.trim(this.channelForm.channel_name) || 'WhatsApp',
      phone_number_id: this.trim(this.channelForm.phone_number_id),
      waba_id: this.trim(this.channelForm.waba_id),
      business_phone_number: this.normalizePhone(this.channelForm.business_phone_number),
      access_token: this.trim(this.channelForm.access_token),
      webhook_verify_token: this.trim(this.channelForm.webhook_verify_token),
      app_secret: this.trim(this.channelForm.app_secret),
      webhook_url: this.trim(this.channelForm.webhook_url),
      status: this.trim(this.channelForm.status || 'ACTIVE')
    };

    this.controlsocialService.saveChannel(payload).subscribe({
      next: (response) => {
        this.savingChannel = false;
        this.channel = response?.channel || null;
        this.channels = this.channel ? [this.channel] : [];
        this.editingChannelId = this.channel ? Number(this.channel.id) : null;
        this.channelForm.access_token = '';
        this.channelForm.webhook_verify_token = '';
        this.channelForm.app_secret = '';
        this.showChannelForm = false;
        this.showFeedback('Canal salvo com sucesso.', 'success');
        this.loadConversations();
      },
      error: (error) => {
        this.savingChannel = false;
        this.showFeedback(error?.error?.error || 'Não foi possível salvar o canal.', 'error');
      }
    });
  }

  toggleChannelForm() {
    this.showChannelForm = !this.showChannelForm;
  }

  editChannel(channel: any) {
    if (!channel) return;
    this.channel = channel;
    this.editingChannelId = Number(channel.id) || null;
    this.channelForm = {
      channel_name: channel.channel_name || 'WhatsApp',
      phone_number_id: channel.phone_number_id || '',
      waba_id: channel.waba_id || '',
      business_phone_number: channel.business_phone_number || '',
      access_token: '',
      webhook_verify_token: '',
      app_secret: '',
      webhook_url: channel.webhook_url || `${window.location.origin}/controlsocial/webhook`,
      status: channel.status || 'ACTIVE'
    };
    this.showChannelForm = true;
  }

  loadConversations() {
    if (!this.tenantId) return;
    this.loadingConversations = true;
    this.controlsocialService
      .listConversations(this.tenantId, {
        status: this.statusFilter || null,
        search: this.search || null,
        channel_id: this.channel?.id || null,
        limit: 100,
        offset: 0
      })
      .subscribe({
        next: (rows) => {
          this.conversations = Array.isArray(rows) ? rows : [];
          if (this.selectedConversation?.id) {
            const found = this.conversations.find((item) => Number(item.id) === Number(this.selectedConversation.id));
            if (found) {
              this.selectedConversation = found;
            }
          }
          this.loadingConversations = false;
        },
        error: (error) => {
          this.loadingConversations = false;
          this.showFeedback(error?.error?.error || 'Não foi possível carregar conversas.', 'error');
        }
      });
  }

  applyConversationFilters() {
    this.loadConversations();
  }

  clearConversationFilters() {
    this.search = '';
    this.statusFilter = '';
    this.loadConversations();
  }

  selectConversation(conversation: any) {
    this.selectedConversation = conversation;
    this.outboundTo = this.resolveConversationPhone(conversation);
    this.loadMessages(conversation.id);
  }

  private resolveConversationPhone(conversation: any): string {
    return this.trim(conversation?.phone_e164 || conversation?.whatsapp_user_id);
  }

  loadMessages(conversationId: number) {
    if (!this.tenantId || !conversationId) return;
    this.loadingMessages = true;
    this.controlsocialService.listMessages(this.tenantId, conversationId, 200, 0).subscribe({
      next: (rows) => {
        this.messages = Array.isArray(rows) ? rows : [];
        this.loadingMessages = false;
      },
      error: (error) => {
        this.loadingMessages = false;
        this.showFeedback(error?.error?.error || 'Não foi possível carregar mensagens.', 'error');
      }
    });
  }

  sendTextMessage() {
    if (!this.tenantId || !this.userId) return;
    const target = this.normalizePhone(this.outboundTo || this.resolveConversationPhone(this.selectedConversation));
    const text = this.trim(this.outboundText);

    if (!target || !text) {
      this.showFeedback('Informe o número destino e a mensagem.', 'error');
      return;
    }

    this.sendingMessage = true;
    this.controlsocialService
      .sendMessage({
        tenant_id: this.tenantId,
        user_id: this.userId,
        channel_id: this.channel?.id || null,
        conversation_id: this.selectedConversation?.id || null,
        to: target,
        text
      })
      .subscribe({
        next: (response) => {
          this.outboundText = '';
          this.showFeedback('Mensagem enviada com sucesso.', 'success');
          this.loadConversations();
          const conversationId = Number(response?.conversation_id || this.selectedConversation?.id || 0);
          if (conversationId) {
            if (!this.selectedConversation || Number(this.selectedConversation.id) !== conversationId) {
              this.selectedConversation = { ...(this.selectedConversation || {}), id: conversationId, phone_e164: target };
            }
            this.loadMessages(conversationId);
          }
          this.sendingMessage = false;
        },
        error: (error) => {
          this.sendingMessage = false;
          this.showFeedback(this.resolveFriendlySendError(error), 'error');
        }
      });
  }

  refreshInboxSilently() {
    if (!this.tenantId || this.activeTab !== 'inbox') return;
    this.controlsocialService
      .listConversations(this.tenantId, {
        status: this.statusFilter || null,
        search: this.search || null,
        channel_id: this.channel?.id || null,
        limit: 100,
        offset: 0
      })
      .subscribe({
        next: (rows) => {
          this.conversations = Array.isArray(rows) ? rows : [];
          if (this.selectedConversation?.id) {
            const found = this.conversations.find((item) => Number(item.id) === Number(this.selectedConversation.id));
            if (found) {
              this.selectedConversation = found;
              this.loadMessages(found.id);
            }
          }
        },
        error: () => {
          // Atualização silenciosa
        }
      });
  }

  formatDate(value: any): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  isOutgoing(message: any): boolean {
    const direction = String(message?.direction || '').toUpperCase();
    const actorType = String(message?.actor_type || '').toUpperCase();
    return direction === 'OUTBOUND' || actorType === 'USER' || actorType === 'BOT';
  }

  messageAuthorLabel(message: any): string {
    if (this.isOutgoing(message)) return 'ControlPS';
    return this.selectedConversation?.profile_name || 'Contato';
  }

  messagePreview(conversation: any): string {
    const text = this.trim(conversation?.last_message_text);
    if (!text) return 'Sem mensagens.';
    return text.length > 70 ? `${text.slice(0, 70)}...` : text;
  }

  messageFailureReason(message: any): string {
    const status = String(message?.status || '').toUpperCase();
    if (status !== 'FAILED') return '';
    return this.resolveFriendlyMetaError(message?.error_message) || 'Falha no envio. Verifique os detalhes no canal da Meta.';
  }

  private resolveFriendlySendError(error: any): string {
    const fromMeta = this.resolveFriendlyMetaError(error?.error?.detail);
    if (fromMeta) return fromMeta;
    return this.trim(error?.error?.error) || 'Falha ao enviar mensagem.';
  }

  private resolveFriendlyMetaError(raw: any): string {
    const entries = this.parseMetaErrorEntries(raw);
    for (const entry of entries) {
      const code = Number(entry?.code || entry?.error?.code || entry?.error_subcode || 0);
      const details = this.trim(entry?.error_data?.details || entry?.message || entry?.title);
      if (code === 131047) {
        return 'Não foi possível enviar: a janela de 24 horas expirou. Envie um template aprovado ou aguarde nova mensagem do cliente.';
      }
      if (code === 190) {
        return 'Token de acesso da Meta inválido ou expirado. Gere um novo token e salve na configuração do canal.';
      }
      if (code === 131026) {
        return 'Número de destino inválido ou sem permissão para receber mensagens neste canal.';
      }
      if (details) {
        return details;
      }
    }
    return '';
  }

  private parseMetaErrorEntries(raw: any): any[] {
    if (Array.isArray(raw)) return raw;
    if (!raw) return [];
    if (typeof raw === 'string') {
      const text = raw.trim();
      if (!text) return [];
      try {
        const parsed = JSON.parse(text);
        return this.parseMetaErrorEntries(parsed);
      } catch (_error) {
        return [{ message: text }];
      }
    }
    if (typeof raw === 'object') {
      if (Array.isArray(raw.errors)) return raw.errors;
      if (raw.error) return [raw.error];
      return [raw];
    }
    return [];
  }
}
